import { translate } from "@classdojo/web/utils/translate";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ToolkitAppScroller from "components/BeyondToolkit/ToolkitAppScroller";
import ToolkitFullscreenAudioPlayer from "components/ToolkitFullscreenAudioPlayer";
import ToolkitGridSplitItem from "components/ToolkitGridSplitItem";
import { closeWebview } from "src/pods/webviewInit";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { MINDFULNESS } from "utils/beyondToolkitTools";
import { sendUsage, fetchQuota } from "utils/freemium";

function Mindfulness() {
  // this tells i18next to load these namespaces
  const { ready } = useTranslation(["dojo.beyond"]);
  const [episodeSelected, setEpisodeSelected] = useState<Episode | null>(null);
  const [quotaReached, setQuotaReached] = useState(false);

  const { title, color, imgSrc } = MINDFULNESS();

  if (!ready) return null;

  return (
    <div>
      {episodeSelected == null ? (
        <ToolkitAppScroller title={title} color={color} imgSrc={imgSrc}>
          <div sx={{ display: "grid", gridGap: "dt_s", margin: "dt_s", gridTemplateColumns: "1fr 1fr" }}>
            {getEpisodes().map((episode, i) => {
              return (
                <ToolkitGridSplitItem
                  key={i}
                  index={i}
                  title={translate("dojo.beyond:toolkit.mindfulness.day", { number: i + 1 })}
                  subTitle={episode.subTitle}
                  imgSrc={episode.imgSrc}
                  onItemClick={() => {
                    logParentToolkitEvent({ eventName: `mindfulness.day${i + 1}.tap` });
                    setEpisodeSelected(episode);
                  }}
                />
              );
            })}
          </div>
        </ToolkitAppScroller>
      ) : (
        <ToolkitFullscreenAudioPlayer
          audioSrc={episodeSelected.audioSrc}
          imgSrc={episodeSelected.imgSrc}
          onClose={() => {
            setEpisodeSelected(null);
            if (quotaReached) {
              closeWebview();
            }
          }}
          sendUsage={async () => {
            await sendUsage("bedtimeMeditations");
            const quota = await fetchQuota();

            if (!quota.bedtimeMeditations.available) {
              setQuotaReached(true);
            }
          }}
        />
      )}
    </div>
  );
}

export default Mindfulness;

type Episode = {
  subTitle: string;
  imgSrc: string;
  audioSrc: string;
};

const getEpisodes = () => {
  return [
    {
      subTitle: translate("dojo.beyond:toolkit.mindfulness.episode_titles.the_bell"),
      imgSrc: "/static/images/mindfulness_day1.png",
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-1.mp3",
    },
    {
      subTitle: translate("dojo.beyond:toolkit.mindfulness.episode_titles.calm_breathing"),
      imgSrc: "/static/images/mindfulness_day2.png",
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-2.mp3",
    },
    {
      subTitle: translate("dojo.beyond:toolkit.mindfulness.episode_titles.notice_your_focus"),
      imgSrc: "/static/images/mindfulness_day3.png",
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-3.mp3",
    },
    {
      subTitle: translate("dojo.beyond:toolkit.mindfulness.episode_titles.your_busy_mind"),
      imgSrc: "/static/images/mindfulness_day4.png",
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-4.mp3",
    },
    {
      subTitle: translate("dojo.beyond:toolkit.mindfulness.episode_titles.mindful_listening"),
      imgSrc: "/static/images/mindfulness_day5.png",
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-5.mp3",
    },
    {
      subTitle: translate("dojo.beyond:toolkit.mindfulness.episode_titles.five_things"),
      imgSrc: "/static/images/mindfulness_day6.png",
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-6.mp3",
    },
    {
      subTitle: translate("dojo.beyond:toolkit.mindfulness.episode_titles.whats_the_weather"),
      imgSrc: "/static/images/mindfulness_day7.png",
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-7.mp3",
    },
    {
      subTitle: translate("dojo.beyond:toolkit.mindfulness.episode_titles.color_of_calm"),
      imgSrc: "/static/images/mindfulness_day8.png",
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-8.mp3",
    },
    {
      subTitle: translate("dojo.beyond:toolkit.mindfulness.episode_titles.body_scanning"),
      imgSrc: "/static/images/mindfulness_day9.png",
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-9.mp3",
    },
    {
      subTitle: translate("dojo.beyond:toolkit.mindfulness.episode_titles.your_white_belt"),
      imgSrc: "/static/images/mindfulness_day10.png",
      audioSrc: "https://static.classdojo.com/toolkit-static/mindfulness/110daymindfulness-10.mp3",
    },
  ];
};
